/**
 * React hooks for specialist onboarding operations.
 * @module hooks/use-onboarding
 *
 * @description
 * Provides hooks for:
 * - Fetching onboarding queue (tenants in onboarding status)
 * - Managing onboarding sessions and form data
 * - Specialist setup workflow
 * - Phone provisioning and go-live
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { WEBHOOK_FINALIZE_ONBOARDING } from '@/lib/constants';

// ============ Types ============

/**
 * Database CHECK constraint values for tenants.onboarding_status
 * MUST match: pending, survey_complete, provisioning, testing, active, paused, cancelled
 * Updated Phase 1: Added survey_complete between pending and provisioning
 */
export type TenantOnboardingStatus =
  | 'pending'          // Initial state after payment
  | 'survey_complete'  // Survey submitted, awaiting setup call booking
  | 'provisioning'     // In progress (specialist configuring)
  | 'testing'          // Specialist testing before go-live
  | 'active'           // Fully configured and live
  | 'paused'           // Temporarily paused
  | 'cancelled';       // Subscription cancelled

/**
 * Workflow stages for specialist UI (not stored in DB, used for filtering)
 * Derived from session.current_step and session.status
 */
export type OnboardingWorkflowStage =
  | 'survey_pending'    // Needs to complete survey
  | 'call_pending'      // Survey done, needs call booking
  | 'call_scheduled'    // Call booked
  | 'setup_in_progress' // Call done, specialist configuring
  | 'go_live_ready';    // Ready to activate

export interface OnboardingTenant {
  id: string;
  name: string;
  slug: string;
  industry: string | null;
  industries: string[] | null;
  business_phone: string | null;
  business_address: string | null;
  website: string | null;
  region: string | null;
  city: string | null;
  province: string | null;
  contact_email: string | null;
  owner_name: string | null;
  contact_phone: string | null;
  stripe_customer_id: string | null;
  subscription_tier: string | null;
  subscription_status: string | null;
  onboarding_status: string | null;
  vapi_assistant_id: string | null;
  vapi_phone_number: string | null;
  vapi_phone_number_id: string | null;
  source_demo_id: string | null;
  created_at: string;
  updated_at: string;
  // Relations
  onboarding_session?: OnboardingSession | null;
  source_demo?: {
    id: string;
    business_name: string;
    extracted_services: unknown[] | null;
    website_data: unknown | null;
    sales_agent_name: string | null;
  } | null;
}

export interface OnboardingSession {
  id: string;
  tenant_id: string;
  session_token: string;
  current_step: string;
  total_steps_completed: number;
  progress_percentage: number;
  staged_data: OnboardingStagedData;
  initiated_by: string | null;
  time_spent_minutes: number;
  last_activity_at: string;
  status: 'in_progress' | 'paused' | 'completed' | 'abandoned' | 'expired';
  // Specialist call tracking
  call_scheduled_at?: string | null;
  call_completed_at?: string | null;
  specialist_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OnboardingStagedData {
  // Pre-call survey data
  business_info?: {
    business_name?: string;
    industry?: string;
    website?: string;
    business_phone?: string;
    service_area?: string[];
  };
  contact_details?: {
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    emergency_contact?: string;
  };
  team_structure?: {
    team_size?: 'solo' | '2-3' | '4-6' | '7+';
    technicians?: Array<{
      name: string;
      phone: string;
      specialties: string[];
    }>;
  };
  scheduling?: {
    google_calendar_email?: string;
    business_hours?: Record<string, { open: string; close: string; enabled: boolean }>;
    appointment_duration?: '30' | '60' | '90' | '120';
    offers_emergency_service?: boolean;
    emergency_surcharge?: number;
    emergency_types?: string[];
  };
  // Specialist setup data
  services_pricing?: Array<{
    name: string;
    description?: string;
    price_range?: string;
    confirmed?: boolean;
  }>;
  coverage?: {
    service_areas?: string[];
    response_time?: string;
    emergency_escalation?: 'transfer' | 'book_asap' | 'notification';
    on_call_contact?: string;
  };
  routing?: {
    call_handling_mode?: string;
    ring_timeout?: number;
    after_hours_mode?: string;
  };
  ai_voice?: {
    voice_id?: string;
    greeting?: string;
    preview_audio_url?: string;
  };
  // Business context for AI prompt enrichment (Phase 1 addition)
  business_context?: {
    about?: string;
    pricing_details?: string;
    special_notes?: string;
    unique_selling_points?: string[];
  };
  // AI-specific instructions for prompt customization
  ai_instructions?: string;
  // FAQ pairs for the AI assistant knowledge base
  faqs?: Array<{ question: string; answer: string; category?: string }>;
  // Call notes
  call_notes?: string;
  test_checklist?: {
    greeting_natural?: boolean;
    booking_works?: boolean;
    calendar_created?: boolean;
    confirmation_sent?: boolean;
  };
  // Specialist progress tracking
  completed_phases?: string[];
}

export interface UpdateOnboardingSessionRequest {
  current_step?: string;
  staged_data?: Partial<OnboardingStagedData>;
  specialist_notes?: string;
  call_scheduled_at?: string | null;
  call_completed_at?: string | null;
}

// ============ Query Keys ============

export const onboardingKeys = {
  all: ['onboarding'] as const,
  queue: () => [...onboardingKeys.all, 'queue'] as const,
  queueFiltered: (statuses: TenantOnboardingStatus[]) => [...onboardingKeys.queue(), statuses] as const,
  tenant: (id: string) => [...onboardingKeys.all, 'tenant', id] as const,
  session: (sessionId: string) => [...onboardingKeys.all, 'session', sessionId] as const,
  stats: () => [...onboardingKeys.all, 'stats'] as const,
};

// ============ Queue Hooks ============

/**
 * Fetches tenants in onboarding status for the specialist queue.
 * Includes onboarding session data and source demo info.
 *
 * CRITICAL-3 FIX: Changed filter from subscription_status='onboarding' to
 * onboarding_status IN ('pending', 'provisioning') because:
 * - Stripe webhook sets subscription_status='active' on payment
 * - We track onboarding progress via onboarding_status column
 * - Database CHECK constraint values: pending, provisioning, active, paused, cancelled
 */
export function useOnboardingQueue(statuses?: TenantOnboardingStatus[]) {
  return useQuery<OnboardingTenant[]>({
    queryKey: onboardingKeys.queueFiltered(statuses || []),
    queryFn: async () => {
      const query = supabase
        .from('tenants')
        .select(`
          *,
          onboarding_session:onboarding_sessions(*)
        `)
        // CRITICAL-3 FIX: Filter by onboarding_status (pending/survey_complete/provisioning = needs work)
        // NOT subscription_status which is 'active' after payment
        .in('onboarding_status', statuses && statuses.length > 0 ? statuses : ['pending', 'survey_complete', 'provisioning'])
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // Fetch source demo data for each tenant
      const tenantsWithDemo = await Promise.all(
        (data || []).map(async (tenant) => {
          if (tenant.source_demo_id) {
            const { data: demo } = await supabase
              .from('demos')
              .select('id, business_name, extracted_services, website_data, sales_agent_name')
              .eq('id', tenant.source_demo_id)
              .single();
            return {
              ...tenant,
              onboarding_session: tenant.onboarding_session?.[0] || null,
              source_demo: demo,
            };
          }
          return {
            ...tenant,
            onboarding_session: tenant.onboarding_session?.[0] || null,
            source_demo: null,
          };
        })
      );

      return tenantsWithDemo as OnboardingTenant[];
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Fetches a single tenant with full onboarding data.
 */
export function useOnboardingTenant(tenantId: string) {
  return useQuery<OnboardingTenant | null>({
    queryKey: onboardingKeys.tenant(tenantId),
    queryFn: async () => {
      const { data: tenant, error } = await supabase
        .from('tenants')
        .select(`
          *,
          onboarding_session:onboarding_sessions(*)
        `)
        .eq('id', tenantId)
        .single();

      if (error) throw error;
      if (!tenant) return null;

      // Fetch source demo if exists
      let source_demo = null;
      if (tenant.source_demo_id) {
        const { data: demo } = await supabase
          .from('demos')
          .select('id, business_name, extracted_services, website_data, sales_agent_name')
          .eq('id', tenant.source_demo_id)
          .single();
        source_demo = demo;
      }

      return {
        ...tenant,
        onboarding_session: tenant.onboarding_session?.[0] || null,
        source_demo,
      } as OnboardingTenant;
    },
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
}

// ============ Session Hooks ============

/**
 * Creates or gets an onboarding session for a tenant.
 */
export function useCreateOnboardingSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tenantId: string) => {
      // Check if session already exists
      const { data: existing } = await supabase
        .from('onboarding_sessions')
        .select('*')
        .eq('tenant_id', tenantId)
        .single();

      if (existing) return existing;

      // Generate session token
      const sessionToken = crypto.randomUUID();

      // Create new session
      const { data, error } = await supabase
        .from('onboarding_sessions')
        .insert({
          tenant_id: tenantId,
          session_token: sessionToken,
          current_step: 'business_info',
          staged_data: {},
          status: 'in_progress',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.tenant(data.tenant_id) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.queue() });
    },
    onError: (error) => {
      toast.error('Failed to create onboarding session', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Updates an onboarding session with new data.
 */
export function useUpdateOnboardingSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      tenantId,
      updates,
    }: {
      sessionId: string;
      tenantId: string;
      updates: UpdateOnboardingSessionRequest;
    }) => {
      // If updating staged_data, merge with existing
      const updateData: Record<string, unknown> = { ...updates };

      if (updates.staged_data) {
        const { data: current } = await supabase
          .from('onboarding_sessions')
          .select('staged_data')
          .eq('id', sessionId)
          .single();

        updateData.staged_data = {
          ...(current?.staged_data || {}),
          ...updates.staged_data,
        };
      }

      updateData.last_activity_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('onboarding_sessions')
        .update(updateData)
        .eq('id', sessionId)
        .select()
        .single();

      if (error) throw error;
      return { session: data, tenantId };
    },
    onSuccess: ({ tenantId }) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.tenant(tenantId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.queue() });
    },
    onError: (error) => {
      toast.error('Failed to update session', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

/**
 * Valid status transitions for tenant onboarding.
 * Enforces state machine to prevent invalid jumps (NEW-C5 fix).
 *
 * Valid database values: pending, provisioning, active, paused, cancelled
 * (from tenants.onboarding_status CHECK constraint)
 */
const VALID_STATUS_TRANSITIONS: Record<TenantOnboardingStatus, TenantOnboardingStatus[]> = {
  pending: ['survey_complete', 'provisioning', 'cancelled'],  // Survey complete or skip to provisioning
  survey_complete: ['provisioning', 'cancelled'],              // Book call → specialist begins
  provisioning: ['testing', 'active', 'cancelled'],            // Can test, go live, or cancel
  testing: ['active', 'cancelled'],                            // Can go live or cancel (from testing)
  active: ['paused', 'cancelled'],                             // Can pause or cancel
  paused: ['active', 'cancelled'],                             // Can resume or cancel
  cancelled: [],                                                // Terminal state - no transitions allowed
};

/**
 * Validates a status transition is allowed.
 */
function isValidStatusTransition(
  currentStatus: TenantOnboardingStatus | null,
  newStatus: TenantOnboardingStatus
): { valid: boolean; reason?: string } {
  // If no current status, only pending is allowed as initial state
  if (!currentStatus) {
    if (newStatus === 'pending') {
      return { valid: true };
    }
    return { valid: false, reason: 'Initial status must be "pending"' };
  }

  // Check if the transition is valid
  const allowedTransitions = VALID_STATUS_TRANSITIONS[currentStatus];
  if (!allowedTransitions) {
    return { valid: false, reason: `Unknown current status: ${currentStatus}` };
  }

  if (!allowedTransitions.includes(newStatus)) {
    return {
      valid: false,
      reason: `Cannot transition from "${currentStatus}" to "${newStatus}". Allowed: ${allowedTransitions.join(', ') || 'none'}`,
    };
  }

  return { valid: true };
}

/**
 * Updates the onboarding status of a tenant.
 * Includes status transition validation to prevent invalid state jumps (NEW-C5 fix).
 */
export function useUpdateOnboardingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      status,
      skipValidation = false, // Escape hatch for admin overrides
    }: {
      tenantId: string;
      status: TenantOnboardingStatus; // Use database-aligned type
      skipValidation?: boolean;
    }) => {
      // NEW-C5: Fetch current status and validate transition
      if (!skipValidation) {
        const { data: tenant, error: fetchError } = await supabase
          .from('tenants')
          .select('onboarding_status')
          .eq('id', tenantId)
          .single();

        if (fetchError) {
          throw new Error(`Failed to fetch tenant: ${fetchError.message}`);
        }

        const validation = isValidStatusTransition(
          tenant.onboarding_status as TenantOnboardingStatus | null,
          status
        );

        if (!validation.valid) {
          throw new Error(`Invalid status transition: ${validation.reason}`);
        }
      }

      const { error } = await supabase
        .from('tenants')
        .update({
          onboarding_status: status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', tenantId);

      if (error) throw error;
      return { tenantId, status };
    },
    onSuccess: ({ tenantId, status }) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.tenant(tenantId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.queue() });
      toast.success(`Status updated to ${formatOnboardingStatus(status)}`);
    },
    onError: (error) => {
      toast.error('Failed to update status', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

// ============ Phone Provisioning Hook ============

/**
 * Provisions a phone number for a tenant via N8N workflow.
 */
export function useProvisionPhone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      province,
      city,
    }: {
      tenantId: string;
      province: 'BC' | 'AB';
      city?: string;
    }) => {
      // Call N8N webhook to provision phone
      const n8nWebhookUrl = import.meta.env.VITE_N8N_PROVISION_PHONE_WEBHOOK;

      if (!n8nWebhookUrl) {
        throw new Error('Phone provisioning webhook not configured');
      }

      const response = await fetch(n8nWebhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,  // N8N expects camelCase
          province,
          city,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Provisioning failed' }));
        throw new Error(error.message || 'Failed to provision phone');
      }

      const result = await response.json();
      return { tenantId, ...result };
    },
    onSuccess: ({ tenantId }) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.tenant(tenantId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.queue() });
      toast.success('Phone number provisioned successfully');
    },
    onError: (error) => {
      toast.error('Failed to provision phone', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

// ============ Finalize Setup Hook ============

export interface FinalizeSetupParams {
  tenantId: string;
  vapiPhoneNumberId: string;
  businessName: string;
  city?: string;
  province?: string;
  industry?: string;
  website?: string;
  vapiGreeting?: string;
  vapiVoiceId?: string;
  services?: Array<{
    name: string;
    description?: string;
    category?: string;
    price_min?: number;
    price_max?: number;
    emergency_available?: boolean;
    seasonal?: boolean;
  }>;
  faqs?: Array<{
    question: string;
    answer: string;
    category?: string;
  }>;
  technicians?: Array<{
    name: string;
    phone?: string;
    email?: string;
    max_daily_jobs?: number;
  }>;
  businessHours?: Record<string, { enabled: boolean; start: string; end: string }>;
  routingConfig?: {
    call_handling_mode?: string;
    ring_timeout_seconds?: number;
    after_hours_mode?: string;
    emergency_escalation_mode?: string;
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
  };
  serviceArea?: string;
  businessContext?: {
    about?: string;
    pricing_details?: string;
    special_notes?: string;
  };
  aiInstructions?: string;
}

/**
 * Creates the Vapi assistant and saves all configuration.
 * Call this BEFORE go-live to set up the AI receptionist.
 */
export function useFinalizeSetup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: FinalizeSetupParams) => {
      if (!WEBHOOK_FINALIZE_ONBOARDING) {
        throw new Error('Finalize onboarding webhook not configured');
      }

      const response = await fetch(WEBHOOK_FINALIZE_ONBOARDING, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: params.tenantId,
          vapi_phone_number_id: params.vapiPhoneNumberId,
          businessName: params.businessName,
          city: params.city,
          province: params.province,
          industry: params.industry,
          website: params.website,
          vapiGreeting: params.vapiGreeting,
          vapiVoiceId: params.vapiVoiceId,
          services: params.services || [],
          faqs: params.faqs || [],
          technicians: params.technicians || [],
          business_hours: params.businessHours,
          routing_config: params.routingConfig,
          serviceArea: params.serviceArea,
          business_context: params.businessContext,
          ai_instructions: params.aiInstructions,
          // Request testing status, not active yet
          set_status: 'call_completed',
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Setup finalization failed' }));
        throw new Error(error.error || error.message || 'Failed to create AI assistant');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to create AI assistant');
      }

      return { tenantId: params.tenantId, assistantId: result.assistant_id, ...result };
    },
    onSuccess: ({ tenantId }) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.tenant(tenantId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.queue() });
      toast.success('AI Assistant created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create AI assistant', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

// ============ Go-Live Hook ============

/**
 * Marks onboarding as complete and activates the tenant.
 */
export function useCompleteOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tenantId: string) => {
      // Update tenant to active
      // NEW-C6 FIX: Use 'active' (valid CHECK constraint value) instead of 'live'
      // Database CHECK: onboarding_status IN ('pending', 'provisioning', 'testing', 'active', 'paused', 'cancelled')
      const { error: tenantError } = await supabase
        .from('tenants')
        .update({
          subscription_status: 'active',
          onboarding_status: 'active',  // Fixed: 'live' is not a valid status value
          activated_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', tenantId);

      if (tenantError) throw tenantError;

      // Mark onboarding session as completed
      const { error: sessionError } = await supabase
        .from('onboarding_sessions')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
        })
        .eq('tenant_id', tenantId);

      if (sessionError) {
        if (import.meta.env.DEV) console.warn('Failed to update session status:', sessionError);
      }

      // Optionally trigger go-live email via N8N
      const n8nGoLiveWebhook = import.meta.env.VITE_N8N_GO_LIVE_WEBHOOK;
      if (n8nGoLiveWebhook) {
        try {
          await fetch(n8nGoLiveWebhook, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tenant_id: tenantId }),
          });
        } catch (webhookError) {
          if (import.meta.env.DEV) console.warn('Failed to trigger go-live webhook:', webhookError);
        }
      }

      return { tenantId };
    },
    onSuccess: ({ tenantId }) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.tenant(tenantId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.queue() });
      toast.success('Tenant is now live!');
    },
    onError: (error) => {
      toast.error('Failed to complete onboarding', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

// ============ Delete Tenant Hook ============

/**
 * Deletes a tenant from the onboarding queue.
 * This permanently removes the tenant and associated onboarding session.
 * Use with caution - typically only for test/duplicate entries.
 */
export function useDeleteOnboardingTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tenantId: string) => {
      // Use RPC function that has SECURITY DEFINER to bypass RLS
      // and handles all foreign key constraints properly
      const { data, error } = await supabase.rpc('delete_onboarding_tenant', {
        p_tenant_id: tenantId,
      });

      if (error) {
        if (import.meta.env.DEV) console.error('Delete tenant RPC error:', error);
        throw error;
      }

      // Check the response from the RPC function
      const result = data as { success: boolean; error?: string; deleted_tenant?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete tenant');
      }

      return { tenantId, deletedName: result.deleted_tenant };
    },
    onSuccess: ({ tenantId, deletedName }) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.queue() });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.stats() });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.tenant(tenantId) });
      toast.success(deletedName ? `${deletedName} removed from queue` : 'Tenant removed from queue');
    },
    onError: (error) => {
      toast.error('Failed to delete tenant', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

// ============ Stats Hook ============

/**
 * Onboarding queue statistics using actual database status values.
 * CRITICAL-2 FIX: Changed to use actual DB status values instead of UI values.
 * DB CHECK constraint values: pending, provisioning, active, paused, cancelled
 */
export interface OnboardingQueueStats {
  total: number;
  pending: number;           // Paid, awaiting survey completion
  survey_complete: number;   // Survey done, awaiting setup call booking
  provisioning: number;      // Specialist configuring
  testing: number;           // Specialist testing before go-live
  active: number;            // Live and operational
  paused: number;            // Temporarily paused
  cancelled: number;         // Subscription cancelled
}

/**
 * Fetches onboarding queue statistics.
 * CRITICAL-2 FIX: Uses correct DB status values and proper filter.
 */
export function useOnboardingStats() {
  return useQuery<OnboardingQueueStats>({
    queryKey: onboardingKeys.stats(),
    queryFn: async () => {
      // CRITICAL-2 FIX: Get ALL tenants (not filtering by subscription_status)
      // because we want to count by onboarding_status which tracks the workflow
      const { data, error } = await supabase
        .from('tenants')
        .select('onboarding_status')
        .not('onboarding_status', 'is', null);

      if (error) throw error;

      // CRITICAL-2 FIX: Use actual DB status values
      const stats: OnboardingQueueStats = {
        total: 0,
        pending: 0,
        survey_complete: 0,
        provisioning: 0,
        testing: 0,
        active: 0,
        paused: 0,
        cancelled: 0,
      };

      (data || []).forEach((tenant) => {
        const status = tenant.onboarding_status as TenantOnboardingStatus;
        if (status && status in stats) {
          stats[status]++;
          // Count pending, survey_complete, provisioning, and testing as "in onboarding queue"
          if (status === 'pending' || status === 'survey_complete' || status === 'provisioning' || status === 'testing') {
            stats.total++;
          }
        }
      });

      return stats;
    },
    staleTime: 60 * 1000,
  });
}

// ============ Helpers ============

/**
 * CRITICAL-1 FIX: Status display mapping.
 * Maps database status values to user-friendly display labels.
 *
 * Database CHECK constraint allows: pending, provisioning, active, paused, cancelled
 */
const STATUS_DISPLAY_MAP: Record<TenantOnboardingStatus, string> = {
  pending: 'Awaiting Survey',       // Customer paid, needs to complete survey
  survey_complete: 'Survey Done',   // Survey submitted, awaiting setup call
  provisioning: 'Setting Up',       // Specialist configuring AI assistant
  testing: 'Testing',               // Specialist testing before go-live
  active: 'Live',                   // Fully operational
  paused: 'Paused',                 // Temporarily suspended
  cancelled: 'Cancelled',           // Subscription cancelled
};

const STATUS_COLOR_MAP: Record<TenantOnboardingStatus, string> = {
  pending: 'bg-blue-500',           // Blue = waiting for customer action
  survey_complete: 'bg-cyan-500',   // Cyan = survey done, book call next
  provisioning: 'bg-yellow-500',    // Yellow = in progress
  testing: 'bg-purple-500',         // Purple = testing phase
  active: 'bg-green-500',           // Green = live
  paused: 'bg-orange-500',          // Orange = attention needed
  cancelled: 'bg-gray-500',         // Gray = inactive
};

/**
 * Formats an onboarding status for display.
 * CRITICAL-1 FIX: Uses database-aligned TenantOnboardingStatus type.
 */
export function formatOnboardingStatus(status: TenantOnboardingStatus | string | null): string {
  if (!status) return 'Unknown';
  return STATUS_DISPLAY_MAP[status as TenantOnboardingStatus] || status;
}

/**
 * Gets the color class for an onboarding status.
 * CRITICAL-1 FIX: Uses database-aligned TenantOnboardingStatus type.
 */
export function getOnboardingStatusColor(status: TenantOnboardingStatus | string | null): string {
  if (!status) return 'bg-gray-500';
  return STATUS_COLOR_MAP[status as TenantOnboardingStatus] || 'bg-gray-500';
}

/**
 * Gets the next step in the onboarding flow.
 * CRITICAL-1 FIX: Uses database-aligned status values.
 *
 * Flow: pending → provisioning → testing → active
 * (paused and cancelled are side states, not part of main flow)
 */
export function getNextOnboardingStep(current: TenantOnboardingStatus | string | null): TenantOnboardingStatus | null {
  const flow: TenantOnboardingStatus[] = [
    'pending',          // After payment
    'survey_complete',  // Survey submitted
    'provisioning',     // Specialist configuring
    'testing',          // Specialist testing before go-live
    'active',           // Go live
  ];
  const currentIndex = flow.indexOf(current as TenantOnboardingStatus);
  if (currentIndex === -1 || currentIndex >= flow.length - 1) return null;
  return flow[currentIndex + 1];
}

/**
 * Determines if a tenant is in the onboarding queue (needs work).
 */
export function isInOnboardingQueue(status: TenantOnboardingStatus | string | null): boolean {
  return status === 'pending' || status === 'survey_complete' || status === 'provisioning';
}
